import type { HeroDefinition, HeroState } from '../../../shared/game/world-types.js';
import { PartyStage } from '../../rendering/entities/party-stage.js';
import { icon } from '../icons.js';
import { hitPointsText, hitPointsWidths } from '../hit-points.js';
import { node } from './character-sheet.js';
import { equipmentSlots, type EquipmentSlot } from './inventory-model.js';
import { dragSource, dropTarget, type DragPayload } from '../supplies/item-slot.js';
import type { EquipmentSlotId } from '../../../shared/game/supply-types.js';

/** Building a figure blocks for a few hundred ms: wait out a snapshot interval first, so the backpack change that comes with it paints at once. */
const REBUILD_DELAY = 150;

/** Stable canvas and equipment slots. Snapshot changes never rebuild the character's mesh. */
export class PaperdollView {
  readonly element = node('aside', 'inventory-paperdoll');
  private viewport = node('div', 'inventory-viewport');
  private equipment = node('div', 'paperdoll-equipment');
  private condition = node('div', 'paperdoll-condition');
  private stage?: PartyStage;
  private appearance = '';
  private equipmentSignature = '';
  private conditionSignature = '';
  private rebuild?: ReturnType<typeof setTimeout>;

  constructor(private inspect: (slot: EquipmentSlot) => void, private accepts: (slot: EquipmentSlotId, payload: DragPayload) => boolean, private equip: (slot: EquipmentSlotId, payload: DragPayload) => void,
    private menu: (slot: EquipmentSlot, from: HTMLElement) => void) {
    // Drag turns the figure, the wheel zooms to the face, double-click (or Home) resets; arrows and +/- do the same from the keyboard.
    this.viewport.setAttribute('aria-label', 'Character paperdoll'); this.viewport.tabIndex = 0;
    this.element.append(this.viewport, this.equipment, this.condition);
  }

  update(definition: HeroDefinition, hero: HeroState) {
    const appearance = JSON.stringify([definition.id, definition.appearance, definition.color, definition.shield, definition.profile.attacks, definition.gear]);
    if (appearance !== this.appearance) {
      this.appearance = appearance;
      clearTimeout(this.rebuild); this.viewport.setAttribute('aria-busy', 'true');
      this.rebuild = setTimeout(() => {
        try { this.stage ??= new PartyStage(this.viewport, 'high', { plinth: true, handled: true }); this.stage.show([definition], new Set([definition.id])); }
        catch { this.viewport.setAttribute('aria-busy', 'false'); this.viewport.replaceChildren(node('p', 'inventory-note', 'Character preview unavailable. Equipment and character details remain available.')); }
      }, this.stage ? REBUILD_DELAY : 0);
    }
    const slots = equipmentSlots(definition, hero), signature = JSON.stringify([hero.id, hero.inventory.equipment, slots]);
    if (signature !== this.equipmentSignature) {
      this.equipmentSignature = signature; this.equipment.replaceChildren();
      for (const slot of slots) {
        const button = node('button', `equipment-slot equipment-slot--${slot.id}${slot.name ? ' equipped' : ' vacant'}`); button.type = 'button'; button.dataset.equipmentSlot = slot.id;
        button.setAttribute('aria-label', `${slot.label}: ${slot.name ?? 'Empty'}`); button.title = `${slot.label}: ${slot.name ?? 'Empty'}`;
        const socket = node('span', 'equipment-socket'); socket.append(icon(slot.glyph));
        button.append(socket, node('span', 'equipment-label', slot.label), node('span', 'equipment-name', slot.name ?? 'Empty'));
        button.addEventListener('click', () => this.inspect(slot)); button.addEventListener('contextmenu', event => { event.preventDefault(); this.menu(slot, button); });
        this.equipment.append(button);
        // Equipped items can be dragged back into the backpack to unequip them.
        const itemId = hero.inventory.equipment?.[slot.id];
        if (itemId) dragSource(button, { from: 'equipped', heroId: hero.id, slot: slot.id, itemId }, `${slot.label}: ${slot.name ?? ''}`);
        dropTarget(button, payload => this.accepts(slot.id, payload), payload => this.equip(slot.id, payload));
      }
    }
    const health = hero.health, conditionSignature = JSON.stringify([health.hp, health.maxHp, health.temporaryHp, health.wounds, health.maxWounds]);
    if (conditionSignature === this.conditionSignature) return; this.conditionSignature = conditionSignature;
    const hp = node('span', '', `${hitPointsText(health)} HP`), wounds = node('span', '', `${health.wounds}/${health.maxWounds} wounds`);
    const bar = node('div', 'paperdoll-health'); bar.setAttribute('role', 'meter'); bar.setAttribute('aria-label', 'Hit points'); bar.setAttribute('aria-valuemin', '0'); bar.setAttribute('aria-valuemax', String(health.maxHp)); bar.setAttribute('aria-valuenow', String(Math.max(0, health.hp)));
    const widths = hitPointsWidths(health), fill = node('span'), ward = node('span', 'temporary'); fill.style.width = `${widths.hp}%`; ward.style.width = `${widths.temporary}%`; bar.append(fill, ward);
    this.condition.replaceChildren(bar, hp, wounds);
  }

  close() { clearTimeout(this.rebuild); this.stage?.dispose(); this.stage = undefined; this.appearance = ''; this.viewport.removeAttribute('aria-busy'); }
}
